import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, MapPin, Sparkles } from "lucide-react";
import { Seo, PageSchema } from "@/components/Seo";
import { ShareButton } from "@/components/ShareButton";
import { ShareBar } from "@/components/ShareBar";
import { ExploreLinks } from "@/components/ExploreLinks";
import { Reveal } from "@/components/motion";

const SITE = "https://cphhighlights.com";
const HERO_IMG =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/41cbdb2920d50115_Screenshot_20261006_195804_Instagram.jpg";
const mapsUrl = (name) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${name}, Copenhagen`)}`;

// Grouped so streets in the same neighbourhood sit together — each is a short
// walk from its neighbours, so a group doubles as a mini browsing route.
const AREAS = [
  {
    area: "Frederiksberg & the Vesterbro border",
    areaId: "frederiksberg",
    highlight: true,
    note: "The strongest starting point if you want that “I found this street myself” feeling — do all three as one easy loop.",
    streets: [
      {
        name: "Gammel Kongevej",
        tags: ["Local", "Upscale"],
        text: "Frederiksberg's long, elegant high street — everyday local shops mixed with a few upscale names, good cafés and the odd design store. Less polished than Strøget, far more how Copenhageners actually shop.",
      },
      {
        name: "Værnedamsvej",
        tags: ["Parisian", "Charming"],
        text: "The ‘little Paris’ street straddling Vesterbro and Frederiksberg — wine shops, a fishmonger, a cheesemonger, florists and tiny boutiques packed into one short, charming block.",
      },
      {
        name: "Tullinsgade",
        tags: ["Quiet", "Independent"],
        text: "Right off Værnedamsvej — a calm side street of small independent shops and cafés that finishes off the little pocket.",
      },
    ],
  },
  {
    area: "Nørrebro",
    areaId: "norrebro",
    streets: [
      {
        name: "Jægersborggade",
        tags: ["Design", "Hip"],
        text: "A 300 m indie strip — ceramics, natural-wine bars, specialty coffee and small design studios, with Assistens Cemetery at one end.",
      },
      {
        name: "Ravnsborggade",
        tags: ["Eclectic", "Vintage"],
        text: "Nørrebro's antiques-and-vintage street — secondhand finds, retro furniture and a laid-back café scene near the lakes.",
      },
      {
        name: "Elmegade",
        tags: ["Small", "Creative"],
        text: "A short, creative lane of boutiques, vintage and brunch spots, a block from Sankt Hans Torv.",
      },
      {
        name: "Blågårdsgade",
        tags: ["Local"],
        text: "Pedestrian, unpretentious and full of neighbourhood life — café tables and small shops around Blågårds Plads.",
      },
    ],
  },
  {
    area: "The Latin Quarter (old town)",
    areaId: "indre-by",
    streets: [
      {
        name: "Larsbjørnstræde",
        tags: ["Tiny", "Independent"],
        text: "The heart of the old ‘Pisserenden’ quarter — tiny independent streetwear, record and vintage shops on medieval cobbles.",
      },
      {
        name: "Studiestræde",
        tags: ["Alternative", "Central"],
        text: "Alternative but dead central — secondhand, records, streetwear and tattoo studios a step off the main drag.",
      },
      {
        name: "Peder Hvitfeldts Stræde",
        tags: ["Hidden", "Atmospheric"],
        text: "A hidden, atmospheric little street near Rundetårn — a handful of antiquarian and curiosity shops on worn cobbles.",
      },
    ],
  },
  {
    area: "Vesterbro",
    areaId: "vesterbro",
    streets: [
      {
        name: "Istedgade",
        tags: ["Authentic", "Alternative"],
        text: "Vesterbro's long, authentic spine — the farther from the station you walk, the better it gets: independent fashion, design, records and coffee with real neighbourhood grit.",
      },
    ],
  },
  {
    area: "Østerbro",
    areaId: "osterbro",
    streets: [
      {
        name: "Nordre Frihavnsgade",
        tags: ["Residential", "Small shops"],
        text: "Residential Østerbro with a string of small local shops, bakeries and cafés — low-key browsing where locals actually live.",
      },
    ],
  },
];

const ALL = AREAS.flatMap((a) => a.streets.map((s) => ({ ...s, area: a.area })));

const ITEM_LIST = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Copenhagen's best shopping streets beyond Strøget",
  description:
    "Eleven of Copenhagen's most characterful shopping and browsing streets beyond Strøget — local, independent and grouped by neighbourhood, from Værnedamsvej to Jægersborggade.",
  itemListElement: ALL.map((s, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: `${s.name}, ${s.area}, Copenhagen`,
    url: `${SITE}/copenhagen-shopping-streets`,
  })),
};

export default function ShoppingStreetsPage() {
  return (
    <main data-testid="shopping-streets-page" className="bg-[#F9F8F6] text-black min-h-screen">
      <Seo
        title="Copenhagen's Best Shopping Streets Beyond Strøget"
        description="Skip Strøget. These are Copenhagen's most characterful shopping and browsing streets — local, independent and grouped by neighbourhood, each with a Google Maps link."
        path="/copenhagen-shopping-streets"
      />
      <PageSchema
        name="Copenhagen's Best Shopping Streets Beyond Strøget"
        description="Copenhagen's most characterful shopping and browsing streets beyond Strøget — local, independent and grouped by neighbourhood."
        path="/copenhagen-shopping-streets"
        crumb="Shopping streets"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(ITEM_LIST)}</script>
      </Helmet>

      {/* hero */}
      <section className="relative bg-[#13141A] text-white overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Café Ipsen & Co on the corner of Gammel Kongevej, Frederiksberg — awnings and pavement tables in the sun"
            className="w-full h-full object-cover object-[center_35%] opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#13141A] via-[#13141A]/70 to-[#13141A]/40" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 pt-24 md:pt-28 pb-12 md:pb-20">
          <div className="flex items-center gap-3 mb-10 md:mb-12">
            <Link
              to="/"
              data-testid="shopping-streets-back"
              className="inline-flex items-center gap-1.5 font-body font-800 text-sm px-3.5 py-2 rounded-full bg-white/10 border border-white/20 hover:bg-white/20 transition-colors"
            >
              <ArrowLeft size={14} /> Back to start
            </Link>
            <ShareButton
              url="/copenhagen-shopping-streets"
              title="Copenhagen's Best Shopping Streets Beyond Strøget"
              data-testid="shopping-streets-share"
              className="bg-white/10 text-white border-white/20 hover:bg-white/20"
            />
          </div>
          <Reveal>
            <p className="font-body font-800 text-xs md:text-sm uppercase tracking-[0.35em] text-white mb-5">
              STREETS WORTH BROWSING
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-7xl">
              Shopping streets
              <br />
              <span className="font-editorial italic font-600">beyond Strøget</span>
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="font-body text-base md:text-lg text-white/70 mt-6 max-w-xl leading-relaxed">
              Strøget is the famous one — but the streets Copenhageners actually
              browse are smaller, more local and far more fun. Here they are, grouped
              by neighbourhood so you can do each cluster on foot.
            </p>
          </Reveal>
        </div>
      </section>

      {/* areas */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-20 space-y-14 md:space-y-20">
        {AREAS.map((group, gi) => (
          <div key={group.area} data-testid={`street-group-${group.areaId}`}>
            <Reveal>
              <div className="flex flex-wrap items-end justify-between gap-3 border-b-2 border-black/10 pb-4 mb-8">
                <div>
                  {group.highlight && (
                    <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226] mb-2">
                      START HERE
                    </p>
                  )}
                  <h2 className="font-display font-900 tracking-tighter leading-[0.95] text-3xl md:text-5xl">
                    {group.area}
                  </h2>
                </div>
                <Link
                  to={`/areas/${group.areaId}`}
                  data-testid={`street-group-area-${group.areaId}`}
                  className="inline-flex items-center gap-1 font-body font-800 text-[11px] tracking-widest uppercase text-[#DF3226] hover:text-[#DF3226] transition-colors"
                >
                  <MapPin size={12} /> Area guide
                </Link>
              </div>
            </Reveal>
            {group.note && (
              <Reveal>
                <p className="font-editorial italic text-base md:text-lg text-black/60 -mt-3 mb-8 max-w-2xl">
                  {group.note}
                </p>
              </Reveal>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {group.streets.map((s, i) => (
                <Reveal key={s.name} y={30} delay={Math.min((i % 2) * 0.06, 0.12)}>
                  <article
                    data-testid={`street-card-${gi}-${i}`}
                    className="h-full bg-white border border-zinc-200 shadow-brut rounded-2xl p-5 md:p-6 flex flex-col"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <a
                        href={mapsUrl(s.name)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`street-maps-${gi}-${i}`}
                        className="font-display font-900 tracking-tight text-2xl md:text-3xl hover:text-[#DF3226] hover:underline underline-offset-4 transition-colors"
                      >
                        {s.name}
                      </a>
                      <a
                        href={mapsUrl(s.name)}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Open ${s.name} in Google Maps`}
                        data-testid={`street-pin-${gi}-${i}`}
                        className="shrink-0 mt-1 inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#F9F8F6] border border-zinc-200 text-[#DF3226] hover:bg-[#DF3226] hover:text-white transition-colors"
                      >
                        <MapPin size={16} />
                      </a>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {s.tags.map((t) => (
                        <span
                          key={t}
                          className="font-body font-700 text-[11px] tracking-wide uppercase text-black/55 bg-[#F9F8F6] border border-zinc-200 rounded-full px-2.5 py-1"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                    <p className="font-body text-sm md:text-base text-black/70 leading-relaxed mt-4 flex-1">
                      {s.text}
                    </p>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        ))}
      </section>

      {/* planner CTA */}
      <section data-testid="shopping-streets-cta" className="bg-[#13141A] text-white border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl md:text-5xl">
            Found your street?
            <br />
            <span className="font-editorial italic font-600">The planner sorts the rest</span>
          </h2>
          <Link
            to="/planner"
            data-testid="shopping-streets-planner-cta"
            className="shrink-0 inline-flex items-center gap-2 font-display font-900 text-lg bg-[#DF3226] text-white px-8 py-4 rounded-full shadow-brut hover:-translate-y-1 hover:shadow-brut-lg transition-all duration-300"
          >
            Open the planner
          </Link>
        </div>
      </section>

      <ExploreLinks
        testid="explore-shopping-streets"
        links={[
          { label: "The prettiest streets to photograph", to: "/photo-worthy-streets" },
          { label: "Every street's neighbourhood — the area guides", to: "/areas" },
          { label: "The ready-made 3 days — Copenhagen itinerary", to: "/copenhagen-itinerary" },
        ]}
      />
      <ShareBar />
    </main>
  );
}
